import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { Loader2, FileSpreadsheet } from 'lucide-react';
import { toast } from 'sonner';
import { JewelleryTagViewer, buildTagDataFromPublic, tagMaterials } from '@/components/jewellery/JewelleryTagViewer';
import { setTagRules, setTagClassOrder } from '@/lib/tagRules';
import { downloadTagsExcel } from '@/lib/tagExcel';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// PUBLIC — a saved tag list shared by link (no password, expires after 7 days).
export default function TagListShareView() {
  const { token } = useParams();
  const [state, setState] = useState({ loading: true, error: '', name: '', view: null, tags: [] });
  useEffect(() => {
    axios.get(`${API}/public/tag-list/${token}`).then(({ data }) => {
      setTagRules(data.tags[0]?.tag_rules || []);
      setTagClassOrder(data.tags[0]?.tag_class_order || []);
      setState({ loading: false, error: '', name: data.name, view: data.view, tags: data.tags.map((t) => ({ ...buildTagDataFromPublic(t), view: t.view })) });
    }).catch((e) => setState((s) => ({ ...s, loading: false, error: e?.response?.data?.detail || 'This link could not be opened.' })));
  }, [token]);
  const excel = () => downloadTagsExcel(state.tags.map((d) => ({ data: d, materials: tagMaterials(d, state.view.rm_mode) })), {
    rmMode: state.view.rm_mode, showPieces: state.view.show_pieces, withRates: false, fileName: `${state.name}.xlsx`,
  }).catch(() => toast.error('Could not build the Excel file.'));
  if (state.loading) return <div className="grid min-h-screen place-items-center"><Loader2 className="animate-spin text-slate-400" /></div>;
  if (state.error) return <div className="grid min-h-screen place-items-center p-6 text-center text-sm text-slate-600" data-testid="shared-list-error">{state.error}</div>;
  return (
    <div className="min-h-screen bg-slate-50 px-4 py-6" data-testid="shared-list-page">
      <div className="mx-auto max-w-3xl space-y-4">
        <div className="text-center text-lg font-bold tracking-wide text-slate-900" data-testid="shared-list-estimate-title">Rough Estimation</div>
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <h1 className="text-xl font-semibold text-slate-900" data-testid="shared-list-name">{state.name}</h1>
            <p className="text-xs text-slate-500">{state.tags.length} piece{state.tags.length === 1 ? '' : 's'}</p>
          </div>
          <button onClick={excel} className="inline-flex h-10 items-center gap-1.5 rounded-lg border border-slate-300 bg-white px-4 text-sm font-semibold text-slate-800 hover:bg-slate-100" data-testid="shared-list-excel-button">
            <FileSpreadsheet size={16} /> Download Excel
          </button>
        </div>
        {state.tags.map((d) => (
          <div key={d.id} className="rounded-2xl border border-slate-200 bg-white p-4" data-testid="shared-list-tag">
            <JewelleryTagViewer data={d} lockPublic publicView={d.view} hideExcel />
          </div>
        ))}
        <p className="pb-4 text-center text-xs italic text-slate-500" data-testid="shared-list-disclaimer">The prices (if any) and the weights are indicative and actuals may differ.</p>
      </div>
    </div>
  );
}
